import {
  ActionButton,
  ActionButtonProps,
  blurRadiusAdjustment,
  breakpoints,
  BreakStyle,
  CollapsibleContainer,
  containerBorderRadius,
  defineStyles,
  defineStylesWithBreaks,
  galleryHeaderHeightNum,
  GLTranslate,
  infoBubble,
  lineClampStyle,
  modalBorderStyle,
  modalContainerStyle,
  modalContainerStyleMobile,
  pagePadding,
  PopoverList,
  PopoverListItem,
  ResolutionBreak,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useDateToElapsedString,
  useGLTranslation,
  useResolutionBreak,
  useThemedStyles,
  useThemedStylesWithBreaks
} from 'notability-services-common-frontend'
import {SVG} from 'notability-services-common-frontend'
import {templateLearnMoreHref} from 'notability-services-common-frontend/src/siteLinks'
import {GalleryNoteContentType, nonFatalAssert, toTitleCase} from 'notability-services-common-universal'
import {TriageState} from 'notability-services-common-universal'
import {useFeatureFlag, useIsMobileApp, useLoggedIn, useUserScreenname} from 'notability-services-next-frontend'
import {CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

import {addingBasePath} from '../config'
import {
  CreateNoteCollectionParams,
  useAddNoteToCollectionMutation,
  useCreateCollectionMutation,
  useNoteViewedMutation
} from '../graphql/graphql'
import {useShowsSocialFeatures} from '../hooks/useShowsSocialFeatures'
import {useTranslateApolloError} from '../hooks/useTranslateApolloError'
import {FollowingContext, useCurrentUserIsFollowing} from '../util/FollowingContext'
import {FECollection} from '../util/GalleryCollectionMapper'
import {IOSMessageHandlers, useSendNativeMessage} from '../util/iosNative'
import {DrillDownNote} from '../util/NoteProps'
import {buildGalleryNoteGraph} from '../util/structuredData'
import {useCustomRouter} from '../util/useCustomRouter'
import {CommentProps} from './CommentView'
import CounterDiv from './CounterDiv'
import DrillDownActions from './DrillDownActions'
import {DrillDownPreview} from './DrillDownPreview'
import DrillDownTagView from './DrillDownTagView'
import DrillDownUserProfile from './DrillDownUserProfile'
import EditMetadata, {contentTypeStrings} from './EditMetadata'
import {FormattedText} from './FormattedText'
import {
  CollectionMetadataModal,
  CommentModal,
  EditModal,
  LikingUsersModal,
  LoginInfoModal,
  Modal,
  ReportNoteModal,
  UnpublishModal
} from './modals'
import {PageMetadata} from './PageMetadata'
import RemixNoteView from './RemixNoteView'
import {SeeGuidelinesForInfo} from './UserProfileLayout'

const maxDescriptionNewlines = 5

/**
 * TODO: would make more sense in [id], but it seems it doesn't init properly, perhaps circular dependency problem.
 */
export const gridHeaderHeight = '40px'
const collapsedDescriptionHeight = 88
const collapseDescriptionForBreaks: ResolutionBreak[] = ['md', 'mobile']

const audioDurationToMinutes = (seconds: number): number => {
  const minutes = seconds / 60
  return minutes > 0 && minutes <= 1 ? 1 : Math.round(minutes)
}

const breakStyles = defineStylesWithBreaks((theme, font) => {
  const actionsContainer: BreakStyle = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopStyle: 'solid',
    borderTopColor: theme.core.b3,
    borderTopWidth: '1px',
    padding: `${commonPaddingSmall} ${commonPaddingMedium}`,
    position: 'sticky',
    bottom: `-${pagePadding.paddingBottom}`,
    mobile: {
      bottom: `-${pagePadding.mobile.paddingBottom}`
    }
  }

  return {
    noteCard: {
      ...theme.elevation.level1,
      position: 'relative',
      top: 0,
      left: 0,
      width: `calc(100% - ${blurRadiusAdjustment}px)`,
      margin: 'auto',
      borderRadius: containerBorderRadius,
      backgroundColor: theme.core.b1,
      display: 'flex',
      flexDirection: 'row',
      mobile: {
        maxWidth: '510px',
        flexDirection: 'column',
        height: '100%'
      },
      md: {
        maxWidth: '510px',
        flexDirection: 'column',
        height: '100%'
      }
    },

    descriptionContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      flex: 3,
      justifyContent: 'flex-start',
      borderLeftStyle: 'solid',
      borderLeftColor: theme.core.b3,
      borderLeftWidth: '1px',
      mobile: {
        flexDirection: 'column',
        borderLeftStyle: 'none',
        borderTopStyle: 'solid',
        borderTopColor: theme.core.b3,
        borderTopWidth: '1px'
      },
      md: {
        flexDirection: 'column',
        borderLeftStyle: 'none',
        borderTopStyle: 'solid',
        borderTopColor: theme.core.b3,
        borderTopWidth: '1px'
      }
    },

    customHeadingLarge: {
      ...font.mediumTitleBold,
      ...lineClampStyle(2),
      color: theme.core.f1,
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      marginRight: commonPaddingLarge,
      paddingBottom: 0
    },

    counterGroup: {
      display: 'flex',
      margin: `${commonPaddingExtraSmall} 0`
    },

    popoverMenu: {
      position: 'absolute',
      mobile: {
        left: '0px'
      }
    },

    reviewMessage: {
      ...infoBubble(theme.alert.container, theme.alert.base),
      textAlign: 'left'
    },

    actionsContainer,

    actionsContainerSticking: {
      ...actionsContainer,
      backgroundColor: theme.core.b1
    },

    actionsStickingThreshold: {
      position: 'absolute',
      bottom: containerBorderRadius,
      height: '1px',
      width: '100%'
    }
  }
})

const commonPaddingExtraSmall = '12px'
const commonPaddingSmall = '16px'
const commonPaddingMedium = '24px'
const commonPaddingLarge = '32px'

const styles = defineStyles((theme, font) => ({
  postedDate: {
    fontSize: '14px',
    textTransform: 'uppercase',
    color: theme.core.f3,
    marginTop: '16px',
    marginBottom: '10px'
  },

  moreLargeColor: {
    color: theme.accent.base
  },

  noteContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'stretch',
    width: '100%',
    maxWidth: `${breakpoints.xl}px`,
    margin: 'auto',
    flexDirection: 'column',
    overflow: 'visible',
    paddingBottom: '75px'
  },

  contentTypeSubtitle: {
    ...font.subhead,
    textTransform: 'uppercase',
    color: theme.accent.base,
    cursor: 'pointer'
  },

  descriptionLink: {
    ...font.bodyBold,
    fontStyle: 'normal',
    color: theme.accent.base,
    cursor: 'pointer',
    display: 'inline-block'
  },

  counterColor: {
    color: theme.core.f3
  },

  openPopover: {
    ...modalBorderStyle(theme),
    background: theme.core.b1,
    zIndex: 10,
    width: 'unset',
    overflow: 'hidden'
  },

  noteMetadataContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    marginBottom: '10px'
  },

  popoverContainer: {
    position: 'relative'
  },

  tagView: {
    marginTop: commonPaddingSmall
  },

  noteText: {
    ...font.body,
    color: theme.core.f1,
    margin: `${commonPaddingSmall} 0 0 0`,
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },

  collectionList: {
    ...theme.elevation.level1,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: containerBorderRadius
  },

  collectionListContainer: {
    margin: '20px',
    ...font.largeTitleBold
  },

  officialCollection: {
    backgroundColor: theme.neutral.container
  },

  description: {
    fontSize: '18px',
    fontWeight: 400,
    color: theme.core.f1,
    minHeight: '30px',
    margin: `${commonPaddingLarge} 0 12px 0`,
    maxWidth: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },

  modalContainer: {
    ...modalContainerStyle(theme),
    ...modalContainerStyleMobile,
    padding: '20px'
  },

  authorPopoverContainer: {
    position: 'absolute',
    width: '260px',
    height: '125px',
    paddingTop: '20px',
    marginTop: '-20px',
    backgroundColor: 'transparent',
    pointerEvents: 'none'
  },

  textHover: {
    textDecoration: 'underline'
  },

  followButton: {
    ...font.subheadBold
  },

  usernameAndFollowButton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    borderBottomStyle: 'solid',
    borderBottomColor: theme.core.b3,
    borderBottomWidth: '1px',
    padding: `8px ${commonPaddingMedium} 8px ${commonPaddingLarge}`
  },

  middleDescriptionArea: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: `${commonPaddingSmall} ${commonPaddingMedium} ${commonPaddingSmall} ${commonPaddingLarge}`
  },

  nonCollapsedDescription: {
    flexGrow: 1
  },

  moreButton: {
    border: 'none'
  },

  tagAndDateSpacer: {
    flexGrow: 1
  },

  contentTypeRow: {
    display: 'flex',
    gap: '8px',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'center'
  },
  toolTipText: {
    ...font.body,
    color: theme.core.f1,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginTop: 'auto',
    marginBottom: 'auto'
  },

  toolTipLink: {
    ...font.bodyBold,
    textTransform: 'uppercase'
  }
}))

export type DrillDownNoteViewProps = DrillDownNote & {
  myCollections: FECollection[]
  collectionsWithNote: string[]
}

export default function DrillDownNoteView({
  tags,
  description,
  descriptionScreennamesWithUserDetails,
  likes,
  name,
  id,
  remixes,
  postedDate,
  language,
  parentNote,
  rootNote,
  images,
  username,
  authorDisplayName,
  authorUserId,
  pageCount,
  audioDurationSeconds,
  avatar,
  triageState,
  underFlaggingReview,
  myCollections,
  collectionsWithNote,
  contentType,
  numComments,
  authorProfileHidden,
  commentsEnabled,
  clientId,
  hasNtb,
  hasNote,
  noteText
}: DrillDownNoteViewProps) {
  const {t, i18n} = useGLTranslation()
  const translateApolloError = useTranslateApolloError()
  const router = useCustomRouter()

  const [isPopoverOpened, setPopoverOpened] = useState(false)
  const [isLoginModalShown, setLoginModalShown] = useState(false)
  const [isCollectionModalShown, setCollectionModalShown] = useState(false)
  const [isCollectionCreateModalShown, setCollectionCreateModalShown] = useState(false)
  const [isEditModalShown, setEditModalShown] = useState(false)
  const [isUnpublishModalShown, setUnpublishModalShown] = useState(false)
  const [isLikersModalShown, setLikersModalShown] = useState(false)
  const [isCommentModalShown, setIsCommentModalShown] = useState(false)
  const [isDownloadPopoverOpened, setDownloadPopoverOpened] = useState(false)
  const [isFlaggedReasonPopoverOpened, setFlaggedReasonPopoverOpened] = useState(false)
  const [numLikes, setNumLikes] = useState(likes)
  const [localDescription, setLocalDescription] = useState(description)
  const [localTitle, setLocalTitle] = useState(name)
  const [localContentType, setLocalContentType] = useState(contentType)
  const [localTags, setLocalTags] = useState(tags)
  const [localLanguage, setLocalLanguage] = useState(language)
  const [localMentions, setLocalMentions] = useState(descriptionScreennamesWithUserDetails)
  const [localCommentsEnabled, setLocalCommentsEnabled] = useState(commentsEnabled)
  const [localNumComments, setLocalNumComments] = useState(numComments)

  const [popoverHeight, setPopoverHeight] = useState(0)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (popoverRef.current && isPopoverOpened) setPopoverHeight(popoverRef.current.clientHeight)
  }, [setPopoverHeight, popoverRef, isPopoverOpened])

  const stickingThresholdRef = useRef<HTMLDivElement>(null)
  const [actionsAreSticking, setActionsAreSticking] = useState(false)
  const [flipOptionsPopover, setFlipOptionsPopover] = useState(false)

  useEffect(() => {
    const stickingThreshold = stickingThresholdRef.current
    if (!stickingThreshold) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          setActionsAreSticking(e ? e.intersectionRatio < 1 : false)
        }
      },
      {threshold: [1]}
    )

    const popoverPositionObserver = new IntersectionObserver(
      ([e]) => {
        const scrollPositionAboveThreshold = e ? e.boundingClientRect.top > galleryHeaderHeightNum : false
        setFlipOptionsPopover(e ? e.intersectionRatio < 1 && scrollPositionAboveThreshold : false)
      },
      {rootMargin: `0px 0px -${popoverHeight / 2}px 0px`}
    )

    observer.observe(stickingThreshold)
    popoverPositionObserver.observe(stickingThreshold)

    return function () {
      observer.unobserve(stickingThreshold)
      popoverPositionObserver.unobserve(stickingThreshold)
    }
  }, [popoverHeight])

  // Determine which collections already have this note.
  const collectionData = myCollections.reduce(
    (acc, val) => {
      if (collectionsWithNote.findIndex((id) => val.id === id) >= 0) {
        acc.usedCollections.push(val)
      } else {
        acc.availableCollections.push(val)
      }
      return acc
    },
    {
      availableCollections: [] as FECollection[],
      usedCollections: [] as FECollection[]
    }
  )

  const [localAvailableCollections, setLocalAvailableCollections] = useState<FECollection[]>(
    collectionData.availableCollections
  )

  const [localUsedCollections, setLocalUsedCollections] = useState<FECollection[]>(collectionData.usedCollections)
  const [noteComments, setNoteComments] = useState<CommentProps[]>([])
  const [commentOffset, setCommentOffset] = useState(0)
  const [viewNote] = useNoteViewedMutation()
  const resBreak = useResolutionBreak()
  const [collapseDescription, setCollapseDescription] = useState(collapseDescriptionForBreaks.indexOf(resBreak) >= 0)
  useEffect(() => {
    setCollapseDescription(collapseDescriptionForBreaks.indexOf(resBreak) >= 0)
  }, [resBreak])
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [addNoteToCollection] = useAddNoteToCollectionMutation({
    onCompleted: (res) => {
      setLocalUsedCollections([...localUsedCollections, res.addNoteToCollection as FECollection])
      setLocalAvailableCollections(
        localAvailableCollections.filter((collection) => collection.name !== res.addNoteToCollection.name)
      )
    },
    onError: (error) => {
      setErrorMessages(translateApolloError(error))
    }
  })
  const [createCollection] = useCreateCollectionMutation({
    onCompleted: (res) => {
      setCollectionCreateModalShown(false)
      void addNoteToCollection({
        variables: {
          addNoteToCollectionParams: {
            id: res.createCollection,
            noteId: id
          }
        }
      })
    },
    onError: (error) => {
      setErrorMessages(translateApolloError(error))
    }
  })

  useEffect(() => {
    void viewNote({
      variables: {viewedNoteParams: {noteId: id}}
    })
  }, [id, viewNote])

  const {loggedIn, userId} = useLoggedIn()
  const {screenname: loggedInScreenname} = useUserScreenname()
  const currentUserIsAuthor = loggedInScreenname === username

  const isInApp = useIsMobileApp()
  const showsSocialFeatures = useShowsSocialFeatures()
  const presentNativeLogin = useSendNativeMessage(IOSMessageHandlers.presentLoginHandler)

  const showLoginModal = useCallback(() => {
    if (isInApp) {
      presentNativeLogin(true)
    } else {
      setLoginModalShown(true)
    }
  }, [presentNativeLogin, setLoginModalShown, isInApp])

  const doReportNote = () => {
    if (!loggedIn) {
      showLoginModal()
    } else {
      setFlaggedReasonPopoverOpened(true)
    }
    setPopoverOpened(false)
  }

  const bs = useThemedStylesWithBreaks(breakStyles)
  const s = useThemedStyles(styles)
  const allowAdminTools = useFeatureFlag('admin_tools')
  const allowCollections = useFeatureFlag('collections')

  const pageCounterIcon = <SVG.PagesMedium {...s.counterColor} />
  const likeCounterIcon = <SVG.LikeSmall {...s.counterColor} />
  const audioDurationCounterIcon = <SVG.AudioSmall {...s.counterColor} />

  let reviewMessage
  if (
    underFlaggingReview ||
    triageState === TriageState.AWAITING_TRIAGE ||
    triageState === TriageState.QUARANTINED ||
    authorProfileHidden
  ) {
    const generalMessage = authorProfileHidden
      ? t('noteProfileHiddenMessage')
      : triageState === TriageState.QUARANTINED
        ? t('noteQuarantinedMessage')
        : underFlaggingReview
          ? t('noteReviewPendingMessage')
          : t('noteAwaitingTriageMessage')
    reviewMessage = (
      <div style={bs.reviewMessage}>
        {generalMessage} <SeeGuidelinesForInfo />
      </div>
    )
  }

  const popoverItems: PopoverListItem[] = []
  const additionalActions: ActionButtonProps[] = []

  if (allowCollections) {
    additionalActions.push({
      key: 'add-to-collection',
      appearance: 'stroked',
      color: 'neutral',
      buttonShape: 'rounded',
      size: 'small',
      onClick: () => setCollectionModalShown(true),
      iconCreator: (color) => <SVG.PlusSmall color={color} />
    })
  }

  if (currentUserIsAuthor) {
    popoverItems.push({
      content: t('noteOptionEdit'),
      clickAction: () => {
        setEditModalShown(true)
        setPopoverOpened(false)
      }
    })
    popoverItems.push({
      content: t('noteOptionUnpublish'),
      theme: 'alert',
      clickAction: () => {
        setUnpublishModalShown(true)
        setPopoverOpened(false)
      }
    })
  } else {
    popoverItems.push({
      content: t('noteOptionReport'),
      theme: 'alert',
      clickAction: () => {
        doReportNote()
        setPopoverOpened(false)
      }
    })
  }

  if (allowAdminTools) {
    popoverItems.push({
      content: t('adminPageView'),
      theme: 'alert',
      clickAction: () => {
        router.pushNote(id, name, true)
      }
    })
  }

  const templateTypeHeader = useMemo(
    () =>
      localContentType === GalleryNoteContentType.TEMPLATE && (
        <div style={s.contentTypeRow}>
          <div style={s.contentTypeSubtitle} onClick={() => router.pushContentType(localContentType, t('Templates'))}>
            {t(contentTypeStrings[localContentType])}
          </div>
          <TooltipProvider>
            <TooltipTrigger>
              <img src={addingBasePath('/help-button.png')} alt={t('showTip')} width="18px" height="18px" />
            </TooltipTrigger>
            <TooltipContent variant="bottom">
              <div style={s.toolTipText}>
                <div>{t('templateTooltip')}</div>
                <div style={s.toolTipLink}>
                  <GLTranslate i18nKey="learnMoreTemplateLink">
                    Read all guidelines
                    <a className={'fake-link'} href={templateLearnMoreHref}>
                      here
                    </a>
                    .
                  </GLTranslate>
                </div>
              </div>
            </TooltipContent>
          </TooltipProvider>
        </div>
      ),
    [localContentType, router, s.contentTypeRow, s.contentTypeSubtitle, s.toolTipLink, s.toolTipText, t]
  )

  const formattedDate = useDateToElapsedString(new Date(postedDate))

  const isOwnProfile = loggedInScreenname === username
  const following = useCurrentUserIsFollowing(authorUserId)
  const {followUser, unfollowUser} = useContext(FollowingContext)
  const canFollow = showsSocialFeatures && !isOwnProfile && following !== undefined

  const followButton = canFollow && (
    <ActionButton
      text={following ? t('unfollow') : t('follow')}
      appearance="tinted"
      size="small"
      onClick={() => {
        if (loggedIn) {
          if (following) {
            unfollowUser(authorUserId)
          } else {
            followUser(authorUserId)
          }
        } else {
          if (following) {
            nonFatalAssert(false, 'Tried to unfollow a user via the UI while logged out')
          }
          showLoginModal()
        }
      }}
    />
  )

  const popoverPositionStyle = useMemo<CSSProperties>(() => {
    return flipOptionsPopover ? {...bs.popoverMenu, top: `-${popoverHeight}px`} : bs.popoverMenu
  }, [flipOptionsPopover, bs.popoverMenu, popoverHeight])

  // JSON-LD for the note. Returns null (→ no markup) for non-public content; see buildGalleryNoteGraph.
  // Reads the local edit state (not the SSR props) so in-place metadata edits update the markup the
  // same way they update the title and OG tags above.
  const noteStructuredData = useMemo(
    () =>
      buildGalleryNoteGraph({
        id,
        name: localTitle,
        description: localDescription,
        images,
        tags: localTags,
        postedDate,
        language: localLanguage,
        likes: numLikes,
        numComments,
        author: username
          ? {screenname: username, displayName: authorDisplayName, avatarUrl: avatar?.largeUrl ?? null}
          : undefined,
        parentNoteId: parentNote?.id ?? null,
        triageState,
        underFlaggingReview,
        authorProfileHidden
      }),
    [
      id,
      localTitle,
      localDescription,
      images,
      username,
      authorDisplayName,
      localTags,
      postedDate,
      localLanguage,
      numLikes,
      numComments,
      avatar?.largeUrl,
      parentNote?.id,
      triageState,
      underFlaggingReview,
      authorProfileHidden
    ]
  )

  return (
    <div
      className="drill-down-note"
      style={s.noteContainer}
      onClick={() => {
        setPopoverOpened(false)
        setDownloadPopoverOpened(false)
      }}
      key={id}
    >
      <PageMetadata
        title={t('pageTitleSub', {pageTitle: toTitleCase(localTitle, i18n.language)})}
        description={localDescription}
        previewImage={images[0]}
        type="article"
        structuredData={noteStructuredData ?? undefined}
      />
      <div style={bs.noteCard}>
        <DrillDownPreview images={images} />
        <div style={bs.descriptionContainer}>
          <div style={s.usernameAndFollowButton}>
            <DrillDownUserProfile
              screenname={username}
              avatarUrl={avatar?.smallUrl}
              linksToProfile={showsSocialFeatures}
            />
            {followButton}
          </div>
          <div style={s.middleDescriptionArea}>
            {templateTypeHeader}
            <div style={bs.customHeadingLarge}>{localTitle}</div>
            <div style={bs.counterGroup}>
              {showsSocialFeatures && (
                <CounterDiv
                  onClick={loggedIn && likes > 0 ? () => setLikersModalShown(true) : undefined}
                  count={numLikes}
                  singleText="like"
                  pluralText="likes"
                  iconDiv={likeCounterIcon}
                />
              )}
              <CounterDiv count={pageCount} singleText="notePage" pluralText="notePages" iconDiv={pageCounterIcon} />
              {audioDurationSeconds > 0 && (
                <CounterDiv
                  count={audioDurationToMinutes(audioDurationSeconds)}
                  singleText="minute"
                  pluralText="minutes"
                  iconDiv={audioDurationCounterIcon}
                />
              )}
            </div>
            {reviewMessage}
            {collapseDescription ? (
              <CollapsibleContainer
                collapsedHeight={collapsedDescriptionHeight}
                text={
                  <FormattedText
                    text={localDescription}
                    usersInText={localMentions}
                    showLoginModal={showLoginModal}
                    maxNewlines={maxDescriptionNewlines}
                  />
                }
              />
            ) : (
              <FormattedText
                text={localDescription}
                usersInText={localMentions}
                showLoginModal={showLoginModal}
                maxNewlines={maxDescriptionNewlines}
              />
            )}
            {noteText && (
              <CollapsibleContainer
                collapsedHeight={collapsedDescriptionHeight}
                text={<p style={s.noteText}>{noteText}</p>}
              />
            )}
            <DrillDownTagView style={s.tagView} tags={localTags} />
            <div style={s.tagAndDateSpacer} />
            <RemixNoteView
              showsSocialFeatures={showsSocialFeatures}
              remixes={remixes}
              thisNoteId={id}
              parentNote={parentNote || undefined}
              rootNote={rootNote || undefined}
            />
            <div style={s.postedDate}>{formattedDate}</div>
          </div>
          <div style={actionsAreSticking ? bs.actionsContainerSticking : bs.actionsContainer}>
            <div style={s.popoverContainer}>
              <ActionButton
                appearance="plain"
                size="small"
                color="neutral"
                style={s.moreButton}
                iconCreator={(color) => <SVG.MoreLarge color={color} />}
                onClick={(e) => {
                  e.stopPropagation()
                  setDownloadPopoverOpened(false)
                  setPopoverOpened((t) => !t)
                }}
              />
              <div style={popoverPositionStyle}>
                {isPopoverOpened && (
                  <PopoverList
                    style={s.openPopover}
                    highlightMouseover={true}
                    items={popoverItems}
                    theme="spillover"
                    contentHeightRef={popoverRef}
                  />
                )}
              </div>
            </div>
            <DrillDownActions
              noteId={id}
              noteName={localTitle}
              hasNtb={hasNtb}
              hasNote={hasNote}
              contentType={localContentType}
              overlayShowing={isDownloadPopoverOpened}
              setOverlayShowing={setDownloadPopoverOpened}
              setSpilloverPopover={setPopoverOpened}
              numLikes={numLikes}
              commentsEnabled={localCommentsEnabled}
              numComments={localNumComments}
              onChangeLike={(status) => {
                setNumLikes(numLikes + (status === 'like' ? 1 : -1))
              }}
              additionalButtons={additionalActions}
              showCommentModal={() => setIsCommentModalShown(true)}
              showLoginModal={showLoginModal}
            />
          </div>
          <div ref={stickingThresholdRef} style={bs.actionsStickingThreshold} />
        </div>
        {
          <LikingUsersModal
            isShown={isLikersModalShown}
            numLikes={likes}
            noteId={id}
            onClose={() => setLikersModalShown(false)}
          />
        }
        {isLoginModalShown ? <LoginInfoModal dismiss={() => setLoginModalShown(false)} /> : null}
        {isCommentModalShown && (
          <CommentModal
            noteId={id}
            noteName={localTitle}
            closeModal={() => setIsCommentModalShown(false)}
            comments={noteComments}
            setComments={setNoteComments}
            offset={commentOffset}
            setOffset={setCommentOffset}
            setNumComments={setLocalNumComments}
            numComments={localNumComments}
            isOwnNote={userId === authorUserId}
          />
        )}
        {isFlaggedReasonPopoverOpened ? (
          <ReportNoteModal
            isVisible={isFlaggedReasonPopoverOpened}
            noteId={id}
            closeModal={() => setFlaggedReasonPopoverOpened(false)}
          />
        ) : null}
        <Modal isShown={isCollectionModalShown} onClickOutside={() => setCollectionModalShown(false)}>
          <div style={s.modalContainer}>
            <EditModal
              onClose={() => setCollectionModalShown(false)}
              title={t('collectionAddModalTitle')}
              errors={errorMessages}
            >
              {localAvailableCollections.length > 0 && (
                <div style={s.collectionListContainer}>
                  <PopoverList
                    style={s.collectionList}
                    highlightMouseover={true}
                    items={localAvailableCollections.map((c) => ({
                      style: c.isOfficialContent ? s.officialCollection : undefined,
                      content: c.name,
                      clickAction: () => {
                        void addNoteToCollection({
                          variables: {
                            addNoteToCollectionParams: {
                              id: c.id,
                              noteId: id
                            }
                          }
                        })
                      }
                    }))}
                  />
                </div>
              )}
              {localUsedCollections.length > 0 && (
                <div style={s.collectionListContainer}>
                  {t('collectionAddUsed')}
                  <PopoverList
                    style={s.collectionList}
                    highlightMouseover={true}
                    items={localUsedCollections.map((c) => ({
                      style: c.isOfficialContent ? s.officialCollection : undefined,
                      content: c.name,
                      clickAction: () => router.pushCollection(c.id)
                    }))}
                  />
                </div>
              )}
              <div style={s.collectionListContainer}>
                {t('collectionAddNew')}
                <PopoverList
                  style={s.collectionList}
                  highlightMouseover={true}
                  items={[
                    {
                      style: s.officialCollection,
                      content: t('createCollectionButton'),
                      clickAction: () => {
                        setCollectionCreateModalShown(true)
                        setCollectionModalShown(false)
                      }
                    }
                  ]}
                />
              </div>
            </EditModal>
          </div>
        </Modal>
        <Modal isShown={isEditModalShown} onClickOutside={() => setEditModalShown(false)}>
          <EditMetadata
            noteId={id}
            isShown={isEditModalShown}
            title={localTitle}
            description={localDescription}
            tags={tags.map((tag) => tag.name)}
            contentType={localContentType}
            setIsShown={setEditModalShown}
            language={localLanguage}
            setParentDescription={setLocalDescription}
            setParentTags={setLocalTags}
            setParentTitle={setLocalTitle}
            setParentLanguage={setLocalLanguage}
            setParentContentType={setLocalContentType}
            setParentUserMentions={setLocalMentions}
            commentsEnabled={localCommentsEnabled}
            setCommentsEnabled={setLocalCommentsEnabled}
          />
        </Modal>
        {isCollectionCreateModalShown ? (
          <CollectionMetadataModal
            title={t('createCollectionButton')}
            doClose={() => {
              setCollectionCreateModalShown(false)
              setCollectionModalShown(true)
            }}
            doSave={(data) => {
              const createNoteCollectionParams: CreateNoteCollectionParams = {
                name: data.name
              }
              if (data.isOfficialContent) {
                createNoteCollectionParams.isOfficialContent = true
                createNoteCollectionParams.officialCollectionId = data.collectionId
              }
              void createCollection({
                variables: {
                  createNoteCollectionParams
                }
              })
              setCollectionModalShown(true)
            }}
            incomingErrors={errorMessages}
          />
        ) : null}
        <Modal isShown={isUnpublishModalShown} onClickOutside={() => setUnpublishModalShown(false)}>
          <UnpublishModal
            dismiss={(unpublished: boolean) => {
              setUnpublishModalShown(false)
              if (unpublished) {
                router.redirectToHomepage()
              }
            }}
            title={localTitle}
            noteId={id}
            image={images[0]!}
            clientId={clientId}
          />
        </Modal>
      </div>
    </div>
  )
}
